import React, { useState, lazy, Suspense } from 'react';
import type { Product } from './ProductModal';

// Dynamic import for modal
const ProductModal = lazy(() => import('./ProductModal'));

// Extended type for internal use to include the custom messaging
interface EnhancedProduct extends Product {
  category: string;
  tagline: string;
  who_is_it_for: string;
  isFlagship?: boolean;
}

const productsData: EnhancedProduct[] = [
  {
    "name": "OpusGo",
    "category": "FOR RETAIL",
    "tagline": "Agile Storefronts",
    "who_is_it_for": "Retailers & Showrooms",
    "logo": "https://supoassets.s3.ap-south-1.amazonaws.com/public/sites/omniviewlabs/assets/icons/OpusGo.webp",
    "description": "Instant in-store visualization tool for dynamic 2D catalogues and lifestyle scenes.",
    "externalUrl": "https://opusgo.omniviewlabs.com",
    "target_audience": ["Furniture Retailers", "Franchise Networks", "D2C Brands"],
    "key_features": [
      "Zero-Skill Catalogue Creator",
      "Real-Time Product Customization",
      "AI Lifestyle Renders",
      "Optimized for in-store tablet and kiosk deployment"
    ],
    "images": [
      "https://supoassets.s3.ap-south-1.amazonaws.com/public/sites/OpusGo/OmniviewSite/Create+2D+catalogues.webp",
      "https://supoassets.s3.ap-south-1.amazonaws.com/public/sites/OpusGo/OmniviewSite/Customize+products.webp",
      "https://supoassets.s3.ap-south-1.amazonaws.com/public/sites/OpusGo/OmniviewSite/Generate+lifestyle.webp"
    ],
    "theme": {
      "color": "#FF4D2D",
      "hoverColor": "#E63E20",
      "textColor": "text-[#FF4D2D]",
      "gradient": "from-[#FF4D2D] to-[#FF8A65]",
      "aura": "shadow-[#FF4D2D]/20"
    },
  },
  {
    "name": "Opus3D",
    "category": "FOR ENTERPRISE",
    "tagline": "Precision Scale",
    "who_is_it_for": "Manufacturers & Large Chains",
    "isFlagship": false,
    "logo": "https://supoassets.s3.ap-south-1.amazonaws.com/public/assets/icon/favicon.png",
    "description": "End-to-end 3D visualization and management engine built for global scale.",
    "target_audience": ["Furniture Manufacturers", "Global Enterprises", "Industrial Designers"],
    "key_features": [
      "High-Fidelity 3D Configurator",
      "Predictive Design AI",
      "Global Asset Management",
      "Scalable digital asset management for global teams"
    ],
    "images": [
      "https://supoassets.s3.ap-south-1.amazonaws.com/public/sites/Opus3D/OmniviewSite/3D+product+customization.webp",
      "https://supoassets.s3.ap-south-1.amazonaws.com/public/sites/Opus3D/OmniviewSite/AI-driven.webp",
      "https://supoassets.s3.ap-south-1.amazonaws.com/public/sites/Opus3D/OmniviewSite/Order+management+system.webp"
    ],
    "theme": {
      "color": "#003B5C",
      "hoverColor": "#002840",
      "textColor": "text-[#003B5C]",
      "gradient": "from-[#003B5C] to-[#005C8F]",
      "aura": "shadow-[#003B5C]/20"
    },
  },
  {
    "name": "OpusStudio",
    "category": "FOR DESIGNERS",
    "tagline": "360 Immersive",
    "who_is_it_for": "Interior Designers & Architects",
    "logo": "https://supoassets.s3.ap-south-1.amazonaws.com/public/sites/omniviewlabs/assets/icons/OpusInteriors.webp",
    "description": "Immersive 3D interior design suite for high-fidelity spatial experiences.",
    "target_audience": ["Architectural Firms", "Interior Designers", "Contract Furnishers"],
    "key_features": [
      "Professional 3D Floor Planning",
      "360° Virtual Walkthroughs",
      "Client-Ready Presentations",
      "Seamless export for client-ready presentations"
    ],
    "images": [
      "https://supoassets.s3.ap-south-1.amazonaws.com/public/sites/OpusStudio/OmniviewSite/Access+to+complete+3D+product.webp",
      "https://supoassets.s3.ap-south-1.amazonaws.com/public/sites/OpusStudio/OmniviewSite/Create+3D+floorplans.webp",
      "https://supoassets.s3.ap-south-1.amazonaws.com/public/sites/OpusStudio/OmniviewSite/walkthrough+experiences.webp"
    ],
    "theme": {
      "color": "#6366F1",
      "hoverColor": "#4F46E5",
      "textColor": "text-[#6366F1]",
      "gradient": "from-[#6366F1] to-[#818CF8]",
      "aura": "shadow-[#6366F1]/20"
    },
    "externalUrl": "https://studio.omniviewlabs.com/"
  }
];

const OptionCard: React.FC<{ product: EnhancedProduct; onClick: () => void }> = ({ product, onClick }) => {
  const theme = product.theme as any;
  const isFlagship = product.isFlagship;
  const isOpus3D = product.name === 'Opus3D';

  return (
    <div
      onClick={onClick}
      className={`
        group relative flex flex-col h-full rounded-[2.5rem] overflow-hidden cursor-pointer transition-all duration-500 border
        ${isFlagship
          ? 'bg-[#021226] border-[#021226] shadow-2xl shadow-blue-900/40 md:scale-105 z-20'
          : `bg-white ${isOpus3D ? 'border-[#003B5C] border-2 shadow-[#003B5C]/10' : 'border-slate-100'} shadow-xl md:hover:-translate-y-2 z-10`
        }
      `}
    >
      {/* Flagship Badge */}
      {isFlagship && (
        <div className="absolute top-0 inset-x-0 flex justify-center z-30">
          <div className="bg-[#FF5C00] text-white text-[9px] font-black uppercase tracking-[0.2em] py-1.5 px-4 rounded-b-xl shadow-lg">
            Most Popular
          </div>
        </div>
      )}

      {/* Header Area */}
      <div className={`pt-8 pb-4 px-8 flex justify-center items-center`}>
        <span className={`text-[9px] font-black uppercase tracking-[0.2em] px-3 py-1 rounded-full ${isFlagship ? 'bg-white/10 text-white border border-white/10' : 'bg-slate-100 text-slate-400'}`}>
          {product.category}
        </span>
      </div>

      <div className="relative z-10 p-8 pt-0 flex flex-col h-full text-center">
        {/* Logo and Name */}
        <div className="flex flex-col items-center gap-4 mb-6">
          <div className={`w-20 h-20 rounded-2xl bg-white shadow-xl flex items-center justify-center p-2 transition-transform duration-500 group-hover:scale-110 ${isFlagship ? 'shadow-white/10' : ''}`}>
            <img src={product.logo} alt={product.name} className="w-full h-full object-contain" />
          </div>
          <div>
            <h3 className={`text-3xl font-black tracking-tighter mb-1.5 ${isFlagship ? 'text-white' : 'text-[#021226]'}`}>{product.name}</h3>
            <p className={`text-[11px] font-black uppercase tracking-[0.2em] ${isFlagship ? 'text-[#FF5C00]' : theme.textColor}`}>{product.tagline}</p>
          </div>
        </div>

        {/* Description - Fixed height for alignment */}
        <p className={`text-[15px] font-semibold leading-relaxed mb-6 min-h-[3.5rem] line-clamp-2 ${isFlagship ? 'text-slate-300' : 'text-slate-600'}`}>
          {product.description}
        </p>

        {/* Feature List Preview */}
        <div className={`space-y-3 mb-6 flex-grow text-left rounded-2xl p-4 ${isFlagship ? 'bg-white/5 border border-white/5' : 'bg-white/50'}`}>
          {product.key_features.slice(0, 3).map((feat, i) => (
            <div key={i} className="flex items-start gap-2.5">
              <svg className={`w-4 h-4 shrink-0 mt-0.5 ${isFlagship ? 'text-[#FF5C00]' : 'text-slate-400'}`} fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
              <span className={`text-xs font-black uppercase tracking-tight leading-snug ${isFlagship ? 'text-slate-200' : 'text-slate-500'}`}>{feat}</span>
            </div>
          ))}
        </div>

        {/* Audience Badge */}
        <div className="mb-6">
          <p className={`text-[10px] font-black uppercase tracking-[0.2em] mb-1.5 ${isFlagship ? 'text-slate-400' : 'text-slate-400'}`}>Best For</p>
          <p className={`text-sm font-black tracking-tight ${isFlagship ? 'text-white' : 'text-slate-900'}`}>{product.who_is_it_for}</p>
        </div>

        {/* CTA Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onClick();
          }}
          className={`w-full py-3.5 rounded-xl font-bold text-[10px] tracking-widest uppercase transition-all duration-300 relative overflow-hidden group/btn flex items-center justify-center gap-2 ${isFlagship ? 'bg-white text-[#021226] hover:bg-slate-100 shadow-xl shadow-white/5' : 'bg-[#021226] text-white hover:bg-[#003B5C]'} active:scale-[0.98] mt-auto`}
        >
          <span className="relative z-10 flex items-center gap-2">
            Learn More
            <svg className="w-3.5 h-3.5 transition-transform duration-500 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          </span>
        </button>
      </div>
    </div>
  );
};

interface ProductSuiteProps {
  onBookDemo: () => void;
}

const ProductSuite: React.FC<ProductSuiteProps> = ({ onBookDemo }) => {
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

  return (
    <section id="products" className="pt-12 pb-12 md:pt-16 md:pb-20 bg-[#F0F4F8] relative overflow-hidden">
      {/* Decorative BG elements */}
      <div className="absolute top-0 right-0 w-1/3 h-1/2 bg-slate-100 opacity-50 skew-x-[-15deg] translate-x-1/2 pointer-events-none"></div>

      {/* Subtle Grid Pattern for entire section to tie it together */}
      <div className="absolute inset-0 bg-grid opacity-[0.3] pointer-events-none mix-blend-multiply"></div>

      <div className="container-xl relative z-10">
        <div className="flex flex-col items-center text-center max-w-4xl mx-auto mb-12 md:mb-20 px-4">
          <span className="inline-block px-4 py-1.5 rounded-full bg-slate-100 text-slate-500 text-[10px] font-black uppercase tracking-[0.4em] mb-6 border border-slate-200">
            Selection Matrix
          </span>
          <h2 data-animation="fade-in-up" className="text-3xl md:text-5xl font-black text-[#021226] leading-tight tracking-tight mb-6">
            Explore Our <span className="text-[#FF5C00]">Product Suite</span>
          </h2>
          <p data-animation="fade-in-up" className="text-lg text-slate-500 stagger-1 font-medium max-w-2xl">
            We've engineered distinct solutions for every stage of the furniture retail value chain. Select the platform that matches your business scale.
          </p>
        </div>

        {/* Options Grid - Vertical on Mobile, Grid on Desktop */}
        <div className="relative mb-20 px-4 md:px-0">
          <div
            className="flex flex-col md:grid md:grid-cols-3 gap-6 lg:gap-8 items-stretch"
          >
            {productsData.map((product, index) => (
              <div key={index} data-animation="fade-in-up" className={`stagger-${index + 1} h-full w-full`}>
                <OptionCard product={product} onClick={() => setSelectedProduct(product)} />
              </div>
            ))}
          </div>
        </div>

        {/* --- Unified "Action Card" CTA --- */}
        <div data-animation="zoom-in" className="mx-4 md:mx-0">
          <div className="bg-[#021226] rounded-[2.5rem] p-10 md:p-16 relative overflow-hidden shadow-2xl text-center md:text-left flex flex-col md:flex-row items-center justify-between gap-10 group">
            {/* Dynamic Backgrounds */}
            <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-[#FF5C00] opacity-10 blur-[150px] rounded-full pointer-events-none group-hover:opacity-20 transition-opacity duration-700"></div>
            <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-10 mix-blend-overlay"></div>

            <div className="relative z-10 max-w-2xl">
              <span className="inline-block px-3 py-1 rounded-full bg-white/10 border border-white/10 text-[#FF5C00] text-[9px] font-black uppercase tracking-[0.3em] mb-4">
                Next Steps
              </span>
              <h3 className="text-3xl md:text-4xl lg:text-5xl font-black text-white tracking-tight leading-tight mb-4">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF5C00] to-orange-300">Want to Know More?</span>
              </h3>
              <p className="text-slate-400 text-base md:text-lg font-medium leading-relaxed">
                Transition to high-velocity 3D retail in record time.
                <span className="hidden md:block text-white mt-2 font-bold md:text-base">Average Onboarding: 14-21 Business Days.</span>
              </p>
            </div>

            <div className="relative z-10 flex-shrink-0 w-full md:w-auto">
              <button
                onClick={onBookDemo}
                className="group relative w-full md:w-auto inline-flex items-center justify-center gap-4 px-10 py-5 bg-[#FF5C00] text-white rounded-2xl font-black text-sm uppercase tracking-widest shadow-[0_20px_50px_-10px_rgba(255,92,0,0.3)] hover:shadow-[0_20px_50px_-10px_rgba(255,92,0,0.5)] hover:-translate-y-1 transition-all duration-300"
              >
                <span className="relative z-10">Schedule a Demo</span>
                <svg className="w-5 h-5 transition-transform group-hover:translate-x-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
                {/* Sheen effect */}
                <div className="absolute inset-0 rounded-2xl overflow-hidden">
                  <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:animate-shimmer"></div>
                </div>
              </button>

              <div className="mt-4 text-center">
                <a
                  href="#demo"
                  className="inline-block font-black text-[11px] uppercase tracking-[0.2em] text-[#FF5C00]/60 hover:text-[#FF5C00] transition-colors"
                >
                  Try the Demo First →
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {
        selectedProduct && (
          <Suspense fallback={null}>
            <ProductModal product={selectedProduct} onClose={() => setSelectedProduct(null)} />
          </Suspense>
        )
      }
    </section >
  );
};

export default ProductSuite;